import './carousel.css';
import YoutubeEmbed from '../../../components/AppVideo/AppVideo';
import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const getFullImageLink = (image: string) => {
  const env = process.env["REACT_APP_ENV_LOADED"];
  let url = `https://content-ndc-portal.aircanada.com`; //default to prod
  if (env === "DEV" || env === "INT" || env === "CRT") {
    url = `https://content-ndc-portal-int.aircanada.com`;
  } else if (env === "BAT" || env === "PREPROD") {
    url = `https://content-ndc-portal-bat.aircanada.com`;
  }
  return url + image;
}




const Carousel: React.FC<{ logoHeight: number }> = ({ logoHeight }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [announcement, setAnnouncement] = useState<string>('');

  const sliderRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isSmallScreen = useMediaQuery('(max-width:480px)');
  const isMediumScreen = useMediaQuery('(max-width:899px)');
  const isLargeScreen = useMediaQuery('(max-width:1024px)');

  const totalSlides = filteredData.length;


  // Determine the base margin value based on the screen size
  let baseMargin;
  if (isSmallScreen) {
    baseMargin = 0; // Example value for small screens
  } else if (isMediumScreen) {
    baseMargin = 5; // Example value for medium screens
  } else if (isLargeScreen) {
    baseMargin = 35; // Example value for large screens
  } else {
    baseMargin = 40; // Default value for extra large screens
  }


  const isValidDateWindow = (startDate: string, endDate: string): boolean => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'America/New_York'
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const formattedDateTimeString = formatter.format(now);
    const parsedDate = new Date(formattedDateTimeString);
    const start = new Date(startDate);
    const end = new Date(endDate);

    return parsedDate >= start && parsedDate <= end;
  };

  const filterDataByDate = (data: any[]): any[] => {
    return data.filter(item => {
      if (item.start_date && item.end_date) {
        return isValidDateWindow(item.start_date, item.end_date);
      }
      return true; // Include items without date properties
    });
  };

  useEffect(() => {
    if (filteredData.length === 1) {
      const style = document.createElement('style');
      style.innerHTML = `
        .slick-slide.slick-active.slick-center.slick-current {
          left: 50% !important;
          transform: translateX(-50%) !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [filteredData.length]);

  useEffect(() => {
    const data: any = t('home_carousel_home_caroussel', {
      returnObjects: true,
      defaultValue: []
    } as any);

    const updateFilteredData = () => {
      const newFilteredData = filterDataByDate(data);
      if (JSON.stringify(newFilteredData) !== JSON.stringify(filteredData)) {
        setFilteredData(newFilteredData);
      }
    };

    updateFilteredData();
    const intervalId = setInterval(updateFilteredData, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Only the actual visible slide stays focusable/exposed; react-slick clones and
  // off-screen slides are hidden via inert to prevent duplicate SR announcements.
  useEffect(() => {
    const applyInert = () => {
      const slides = wrapperRef.current?.querySelectorAll('.slick-slide');
      slides?.forEach((slide) => {
        if (slide.classList.contains('slick-current')) {
          slide.removeAttribute('inert');
          slide.setAttribute('aria-hidden', 'false');
        } else {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    };
    applyInert();
    const raf = requestAnimationFrame(applyInert);
    // Re-apply once the slide transition completes, since `.slick-current` only
    // settles on the new slide after the animation finishes.
    const timeout = window.setTimeout(applyInert, settings.speed + 50);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, filteredData]);

  const handleNext = () => {
    sliderRef.current?.slickNext();
    const nextIndex = (currentIndex + 1) % filteredData.length;
    setCurrentIndex(nextIndex);
    setAnnouncement(t('carouselAnnouncement.showingSlide', { current: nextIndex + 1, total: totalSlides }) ?? '');
  };

  const handlePrev = () => {
    sliderRef.current?.slickPrev();
    const prevIndex = (currentIndex - 1 + filteredData.length) % filteredData.length;
    setCurrentIndex(prevIndex);
    setAnnouncement(t('carouselAnnouncement.showingSlide', { current: prevIndex + 1, total: totalSlides }) ?? '');
  };

  const handleNavigation = (link: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/${i18n.language}${link}`);
    }
  };

  const settings = {
    dots: true,
    infinite: filteredData?.length > 1,
    speed: 500,
    afterChange: (current: number) => {
      setCurrentIndex(current);
    },
    slidesToShow: 1,
    variableWidth: true,
    slidesToScroll: 1,
    centerMode: true,
    arrows: false,
    centerPadding: '0',
    customPaging: (i: number) => (
      <button
        type="button"
        className="carousel-dot-btn"
        aria-label={t('carouselAnnouncement.slideOf', { current: i + 1, total: totalSlides }) ?? `Slide ${i + 1} of ${totalSlides}`}
        aria-current={i === currentIndex ? 'true' : undefined}
      />
    ),
    appendDots: (dots: React.ReactNode) => (
      <ul aria-label={t('carouselAnnouncement.slideNavigation') ?? 'Slide navigation'}>
        {dots}
      </ul>
    ),
    responsive: [
      {
        breakpoint: 2560,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 899,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const cleanCmsText = (html: string): string => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  };
  
  return (
    <div role="region" ref={wrapperRef} className="carousel-wrapper" style={{ marginTop: logoHeight / 2 + baseMargin + 'px', }}>
      <div style={visuallyHidden} aria-live="polite" aria-atomic="true">{announcement}</div>
      <Slider ref={sliderRef} {...settings} className="custom-slider carousel-container">
        {filteredData.map((item, index) => {
          return (
            <div
              key={index}
              style={{ height: 'inherit', width: 'inherit' }}
              role="group"
              aria-roledescription="slide"
              tabIndex={0}
              aria-label={`${index + 1} of ${totalSlides}`}
            >
              {!(item.video === "TRUE") ? (
                <div className="carousel-media-wrapper">
                  <img
                    src={getFullImageLink(isSmallScreen ? item?.media_mobile?.url : item?.media?.url)}
                    alt={isSmallScreen ? item?.media_mobile?.alt : item?.media?.alt}
                    className="carousel-image large"
                  />
                  {item.uri && (
                    <div className="carousel-info">
                      <h2 className={`carousel-title`}> {cleanCmsText(item.title)} </h2>
                      <span className={`carousel-description`} > {cleanCmsText(item.Description)} </span>
                      <button
                        className="carousel-action-btn"
                        role="link"
                        onClick={() => handleNavigation(item.uri.uri)}
                        aria-label={item.button_accessibility}
                      >
                        {item.button}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="carousel-video">
                  <YoutubeEmbed src={item.uri.uri?.split("=")?.[1]} />
                  {item.uri && (
                    <div className="carousel-info">
                      <h2 className={`carousel-title ${!isSmallScreen ? 'half-width' : ''}`}> {cleanCmsText(item.title)} </h2>
                      <span className={`carousel-description ${!isSmallScreen ? 'half-width' : ''}`} > {cleanCmsText(item.Description)} </span>
                      <button
                        className="carousel-action-btn"
                        onClick={() => handleNavigation(item.uri.uri)}
                        aria-label={item.button_accessibility}
                      >
                        {item.button}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Slider>
      {filteredData.length > 1 && (
        <div className="carousel-nav-group">
          <div className='btn-background'>
          <button
            className="carousel-btn left"
            onClick={handlePrev}
            aria-label="Previous"
            tabIndex={0}
          >
            <img src='/images/arrows/chevron-right.png' alt="chevron-left" style={{ padding: '.5rem 0', width: 'inherit', height: 'inherit', rotate: '180deg' }} />
          </button>
          <button
            className="carousel-btn right"
            onClick={handleNext}
            aria-label="Next"
            tabIndex={0}
          >
            <img src='/images/arrows/chevron-right.png' alt="chevron-right" style={{ padding: '.5rem 0', width: 'inherit', height: 'inherit' }} />
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel;
